import { NumberUtils, useDisplayNames } from '@spotify/confidence-core-react';
import { ChangeSetFragment } from '@spotify/confidence-graphql';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';
import {
  MetricDirection,
  MetricKind,
} from '@spotify/plugin-confidence-metrics-react';

import { pluralize } from './utils/extensionHelpers';
import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

interface MetricRole {
  metricKind?: MetricKind;
  minimumDetectableEffect?: number;
  nonInferiorityMargin?: number;
}

interface MetricResult {
  displayName?: string;
  name?: string;
}

interface Metric {
  metric?: string | MetricResult;
  metricRole?: MetricRole;
  preferredDirection?: unknown;
  [key: string]: unknown;
}

interface Filter {
  displayName?: string;
  entity?: string;
  [key: string]: unknown;
}

interface MetricsData {
  metrics?: Metric[];
  bucket?: string;
  entity?: string;
  assignmentTable?: string;
  filters?: Filter[];
  [key: string]: unknown;
}

const getMetricId = (metric?: string | MetricResult): string => {
  if (!metric) {
    return '';
  }

  if (typeof metric === 'string') {
    return metric;
  }

  return metric.name || metric.displayName || '';
};

const isSuccessMetric = (metric: Metric): boolean => {
  return (
    metric.metricRole?.metricKind === MetricKind.SUCCESS ||
    metric.metricRole?.minimumDetectableEffect !== undefined
  );
};

const getMetricTypeLabel = (metricRole?: MetricRole): string => {
  if (!metricRole?.metricKind) {
    return '';
  }

  switch (metricRole.metricKind) {
    case MetricKind.SUCCESS:
      return 'Success metric';
    case MetricKind.GUARDRAIL:
      return 'Guardrail metric';
    default:
      return '';
  }
};

const formatMDE = (mde: number | undefined): string => {
  const percentage = NumberUtils.formatRatioAsPercentage(mde);
  return percentage || 'Not set';
};

const formatDirection = (direction: unknown): string => {
  if (direction === MetricDirection.INCREASE) {
    return 'Increase';
  }
  if (direction === MetricDirection.DECREASE) {
    return 'Decrease';
  }
  return 'Any direction';
};

const hasMetricChanged = (oldMetric: Metric, newMetric: Metric): boolean => {
  // Check if MDE changed (only for success metrics)
  if (isSuccessMetric(oldMetric) && isSuccessMetric(newMetric)) {
    const oldMDE = oldMetric.metricRole?.minimumDetectableEffect;
    const newMDE = newMetric.metricRole?.minimumDetectableEffect;
    if (oldMDE !== newMDE) {
      return true;
    }
  }

  // Check if preferred direction changed
  if (oldMetric.preferredDirection !== newMetric.preferredDirection) {
    return true;
  }

  return false;
};

const formatBucket = (bucket: unknown): string => {
  if (bucket === 'HOURS') return 'Hourly';
  if (bucket === 'DAYS') return 'Daily';
  return String(bucket);
};

const MetricsChangeRender = ({ change }: ActivityChangeOptions) => {
  const { displayNames } = useDisplayNames();

  const oldValue = change.oldValue as MetricsData | null;
  const newValue = change.newValue as MetricsData | null;

  const oldMetrics = oldValue?.metrics ?? [];
  const newMetrics = newValue?.metrics ?? [];

  const oldMetricIds = new Set(oldMetrics.map(m => getMetricId(m.metric)));
  const newMetricIds = new Set(newMetrics.map(m => getMetricId(m.metric)));

  const addedMetrics = newMetrics.filter(
    m => !oldMetricIds.has(getMetricId(m.metric)),
  );
  const removedMetrics = oldMetrics.filter(
    m => !newMetricIds.has(getMetricId(m.metric)),
  );

  const updatedMetrics = newMetrics
    .map(newMetric => {
      const id = getMetricId(newMetric.metric);
      const oldMetric = oldMetrics.find(m => getMetricId(m.metric) === id);

      if (!oldMetric || !hasMetricChanged(oldMetric, newMetric)) {
        return null;
      }

      return { oldMetric, newMetric, id };
    })
    .filter(Boolean) as Array<{
    oldMetric: Metric;
    newMetric: Metric;
    id: string;
  }>;

  const mdeChanges = updatedMetrics.filter(
    ({ oldMetric, newMetric }) =>
      isSuccessMetric(oldMetric) &&
      isSuccessMetric(newMetric) &&
      oldMetric.metricRole?.minimumDetectableEffect !==
        newMetric.metricRole?.minimumDetectableEffect,
  );

  const directionChanges = updatedMetrics.filter(
    ({ oldMetric, newMetric }) =>
      oldMetric.preferredDirection !== newMetric.preferredDirection,
  );

  const oldFilters = oldValue?.filters ?? [];
  const newFilters = newValue?.filters ?? [];

  const oldFilterNames = new Set(oldFilters.map(f => f.displayName));
  const newFilterNames = new Set(newFilters.map(f => f.displayName));

  const addedFilters = newFilters.filter(
    f => !oldFilterNames.has(f.displayName),
  );
  const removedFilters = oldFilters.filter(
    f => !newFilterNames.has(f.displayName),
  );

  // Detect config changes
  const bucketChanged = oldValue?.bucket !== newValue?.bucket;
  const entityChanged = oldValue?.entity !== newValue?.entity;
  const assignmentTableChanged =
    oldValue?.assignmentTable !== newValue?.assignmentTable;

  return (
    <>
      {addedMetrics.length > 0 && (
        <ActivityItem.Section label="Added metrics">
          {addedMetrics.map(metric => {
            const metricTypeLabel = getMetricTypeLabel(metric.metricRole);
            const id = getMetricId(metric.metric);
            const displayName = displayNames.get(id) || id;

            return (
              <ActivityTagItem
                key={id}
                tagLabel={displayName}
                ariaLabel="Metric name"
                newValue={metricTypeLabel}
              />
            );
          })}
        </ActivityItem.Section>
      )}

      {removedMetrics.length > 0 && (
        <ActivityItem.Section label="Removed metrics">
          {removedMetrics.map(metric => {
            const metricTypeLabel = getMetricTypeLabel(metric.metricRole);
            const id = getMetricId(metric.metric);
            const displayName = displayNames.get(id) || id;
            return (
              <ActivityTagItem
                key={id}
                tagLabel={displayName}
                ariaLabel="Metric name"
                defaultValue={metricTypeLabel}
              />
            );
          })}
        </ActivityItem.Section>
      )}

      {mdeChanges.length > 0 && (
        <ActivityItem.Section label="Updated metrics MDE">
          {mdeChanges.map(({ oldMetric, newMetric, id }) => (
            <ActivityTagItem
              key={id}
              tagLabel={displayNames.get(id) || id}
              ariaLabel="Minimum detectable effect change"
              oldValue={formatMDE(
                oldMetric.metricRole?.minimumDetectableEffect,
              )}
              newValue={formatMDE(
                newMetric.metricRole?.minimumDetectableEffect,
              )}
            />
          ))}
        </ActivityItem.Section>
      )}

      {directionChanges.length > 0 && (
        <ActivityItem.Section label="Updated metrics direction">
          {directionChanges.map(({ oldMetric, newMetric, id }) => (
            <ActivityTagItem
              key={id}
              tagLabel={displayNames.get(id) || id}
              ariaLabel="Preferred direction change"
              oldValue={formatDirection(oldMetric.preferredDirection)}
              newValue={formatDirection(newMetric.preferredDirection)}
            />
          ))}
        </ActivityItem.Section>
      )}

      {addedFilters.length > 0 && (
        <ActivityItem.Section label="Added exposure filters">
          {addedFilters.map(filter => (
            <ActivityTagItem
              key={filter.displayName}
              newValue={filter.displayName || 'Unnamed filter'}
              ariaLabel="Exposure filter name"
            />
          ))}
        </ActivityItem.Section>
      )}

      {removedFilters.length > 0 && (
        <ActivityItem.Section label="Removed exposure filters">
          {removedFilters.map(filter => (
            <ActivityTagItem
              key={filter.displayName}
              oldValue={filter.displayName || 'Unnamed filter'}
              ariaLabel="Exposure filter name"
            />
          ))}
        </ActivityItem.Section>
      )}

      {bucketChanged && (
        <ActivityItem.Section label="Calculation interval">
          <ActivityTagItem
            tagLabel="Interval"
            oldValue={formatBucket(oldValue?.bucket)}
            newValue={formatBucket(newValue?.bucket)}
            ariaLabel="Calculation interval change"
          />
        </ActivityItem.Section>
      )}

      {entityChanged && (
        <ActivityItem.Section label="Entity">
          <ActivityTagItem
            tagLabel="Entity"
            oldValue={
              displayNames.get(oldValue?.entity || '') ||
              oldValue?.entity ||
              'Not set'
            }
            newValue={
              displayNames.get(newValue?.entity || '') ||
              newValue?.entity ||
              'Not set'
            }
            ariaLabel="Entity change"
          />
        </ActivityItem.Section>
      )}

      {assignmentTableChanged && (
        <ActivityItem.Section label="Assignment table">
          <ActivityTagItem
            tagLabel="Assignment table"
            oldValue={
              displayNames.get(oldValue?.assignmentTable || '') ||
              oldValue?.assignmentTable ||
              'Not set'
            }
            newValue={
              displayNames.get(newValue?.assignmentTable || '') ||
              newValue?.assignmentTable ||
              'Not set'
            }
            ariaLabel="Assignment table change"
          />
        </ActivityItem.Section>
      )}
    </>
  );
};

const metricsChangePredicate = ({
  change,
}: {
  change: ChangeSetFragment['changes'][number];
}) => change.fieldPath === '/module_data/metrics';

const metricsChangeTitle = (change: ChangeSetFragment['changes'][number]) => {
  const oldValue = change.oldValue as MetricsData | null;
  const newValue = change.newValue as MetricsData | null;

  const oldMetrics = oldValue?.metrics ?? [];
  const newMetrics = newValue?.metrics ?? [];
  const oldFilters = oldValue?.filters ?? [];
  const newFilters = newValue?.filters ?? [];

  const oldMetricIds = new Set(oldMetrics.map(m => getMetricId(m.metric)));
  const newMetricIds = new Set(newMetrics.map(m => getMetricId(m.metric)));
  const metricsAdded = newMetrics.filter(
    m => !oldMetricIds.has(getMetricId(m.metric)),
  ).length;
  const metricsRemoved = oldMetrics.filter(
    m => !newMetricIds.has(getMetricId(m.metric)),
  ).length;

  const oldFilterNames = new Set(oldFilters.map(f => f.displayName));
  const newFilterNames = new Set(newFilters.map(f => f.displayName));
  const filtersAdded = newFilters.filter(
    f => !oldFilterNames.has(f.displayName),
  ).length;
  const filtersRemoved = oldFilters.filter(
    f => !newFilterNames.has(f.displayName),
  ).length;

  if (
    metricsAdded > 0 &&
    metricsRemoved === 0 &&
    filtersAdded === 0 &&
    filtersRemoved === 0
  ) {
    return `Added ${metricsAdded} ${pluralize(metricsAdded, 'metric')}`;
  }

  if (
    metricsRemoved > 0 &&
    metricsAdded === 0 &&
    filtersAdded === 0 &&
    filtersRemoved === 0
  ) {
    return `Removed ${metricsRemoved} ${pluralize(metricsRemoved, 'metric')}`;
  }

  if (
    filtersAdded > 0 &&
    filtersRemoved === 0 &&
    metricsAdded === 0 &&
    metricsRemoved === 0
  ) {
    return `Added ${filtersAdded} exposure ${pluralize(filtersAdded, 'filter')}`;
  }

  if (
    filtersRemoved > 0 &&
    filtersAdded === 0 &&
    metricsAdded === 0 &&
    metricsRemoved === 0
  ) {
    return `Removed ${filtersRemoved} exposure ${pluralize(filtersRemoved, 'filter')}`;
  }

  return 'Updated metrics configuration';
};

export const METRICS_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'metrics-change',
  predicate: metricsChangePredicate,
  title: metricsChangeTitle,
  render: MetricsChangeRender,
});

// Export for testing
export { metricsChangePredicate, metricsChangeTitle };
